import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Reply, Trash2, Edit2, X, Check, Heart } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return Math.floor(s / 60) + "m ago";
  if (s < 86400) return Math.floor(s / 3600) + "h ago";
  if (s < 7 * 86400) return Math.floor(s / 86400) + "d ago";
  return d.toLocaleDateString();
};

const ReplyForm = ({ episodeNumber, parentId, onPosted, onCancel, autoFocus = false }) => {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post("/episodes/" + episodeNumber + "/comments", { body, parent_id: parentId || null });
      setBody("");
      if (onPosted) onPosted();
    } catch (err) {
      const msg = err && err.response && err.response.data && err.response.data.detail;
      toast.error(msg || "could not post");
    } finally { setBusy(false); }
  };
  const testid = parentId ? "reply-form-" + parentId : "comment-form-" + episodeNumber;
  return (
    <form onSubmit={submit} className="flex gap-2 items-start" data-testid={testid}>
      <textarea
        autoFocus={autoFocus}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={parentId ? "reply with your hot take..." : "share your thoughts on this episode..."}
        rows={2}
        maxLength={2000}
        className="flex-1 bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none resize-none"
      />
      <div className="flex flex-col gap-1">
        <button type="submit" disabled={busy || !body.trim()} className="bg-hotpink hover:bg-hotpink_dark text-white font-outfit font-extrabold uppercase tracking-widest text-[10px] px-3 py-2 disabled:opacity-50">
          {busy ? "..." : "post"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-white/50 text-[10px] uppercase tracking-widest hover:text-hotpink py-1">cancel</button>
        )}
      </div>
    </form>
  );
};

const CommentRow = ({ node, episodeNumber, onChanged }) => {
  const { user } = useAuth();
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(node.body);
  const [likeCount, setLikeCount] = useState(node.like_count || 0);
  const [liked, setLiked] = useState(!!node.liked_by_me);
  const [likeBusy, setLikeBusy] = useState(false);
  const isOwn = user && user.id === node.user_id;
  const isDeleted = node.deleted;
  const depth = node._depth || 0;
  const showNested = depth > 0;
  const indentPx = Math.min(depth, 3) * 12;

  const toggleLike = async () => {
    if (!user) { toast.error("log in to like"); return; }
    if (likeBusy || isDeleted) return;
    setLikeBusy(true);
    const prevLiked = liked;
    const prevCount = likeCount;
    setLiked(!prevLiked);
    setLikeCount(prevLiked ? prevCount - 1 : prevCount + 1);
    try {
      const { data } = await api.post("/comments/" + node.id + "/like");
      setLiked(data.liked);
      setLikeCount(data.count);
    } catch (e) {
      setLiked(prevLiked);
      setLikeCount(prevCount);
      toast.error("could not like");
    } finally { setLikeBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete("/comments/" + node.id);
      if (onChanged) onChanged();
    } catch (e) { toast.error("could not delete"); }
  };

  const saveEdit = async () => {
    if (!editBody.trim()) return;
    try {
      await api.patch("/comments/" + node.id, { body: editBody });
      setEditing(false);
      if (onChanged) onChanged();
    } catch (e) { toast.error("could not save"); }
  };

  return (
    <div style={{ marginLeft: indentPx + "px" }} className={showNested ? "border-l-2 border-hotpink/30 pl-4" : ""}>
      <div className="bg-coal border border-white/10 p-3 md:p-4" data-testid={"comment-" + node.id}>
        <div className="flex items-baseline gap-3 mb-2 flex-wrap">
          <span className="scribble text-hotpink text-lg">{node.user_name || "anon"}</span>
          <span className="text-white/40 text-xs font-outfit">{timeAgo(node.created_at)}</span>
          {node.updated_at && node.updated_at !== node.created_at && !isDeleted && (
            <span className="text-white/30 text-[10px] italic">edited</span>
          )}
        </div>
        {editing ? (
          <div className="flex flex-col gap-2">
            <textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              rows={3}
              className="bg-ink border border-white/15 px-3 py-2 text-white font-outfit text-sm focus:border-hotpink focus:outline-none resize-none"
            />
            <div className="flex gap-2">
              <button onClick={saveEdit} className="bg-hotpink text-white text-xs px-3 py-1 uppercase tracking-widest font-bold flex items-center gap-1">
                <Check size={12} /> save
              </button>
              <button onClick={() => { setEditing(false); setEditBody(node.body); }} className="text-white/50 text-xs uppercase tracking-widest hover:text-hotpink flex items-center gap-1">
                <X size={12} /> cancel
              </button>
            </div>
          </div>
        ) : (
          <p className={"font-outfit text-sm md:text-base whitespace-pre-wrap " + (isDeleted ? "text-white/30 italic" : "text-white/90")}>
            {node.body}
          </p>
        )}
        {!isDeleted && !editing && (
          <div className="flex items-center gap-3 mt-2 text-[10px] uppercase tracking-widest font-outfit text-white/50">
            <button onClick={toggleLike} disabled={likeBusy} data-testid={"like-btn-" + node.id} className={"flex items-center gap-1 transition-colors " + (liked ? "text-hotpink" : "hover:text-hotpink")}>
              <Heart size={12} fill={liked ? "currentColor" : "none"} /> {likeCount > 0 ? likeCount : "like"}
            </button>
            {user && (
              <button onClick={() => setReplying(!replying)} data-testid={"reply-btn-" + node.id} className="hover:text-hotpink flex items-center gap-1">
                <Reply size={11} /> reply
              </button>
            )}
            {isOwn && (
              <button onClick={() => setEditing(true)} className="hover:text-hotpink flex items-center gap-1">
                <Edit2 size={11} /> edit
              </button>
            )}
            {isOwn && (
              <button onClick={remove} className="hover:text-red-400 flex items-center gap-1">
                <Trash2 size={11} /> delete
              </button>
            )}
          </div>
        )}
      </div>
      {replying && (
        <div className="mt-2 mb-3">
          <ReplyForm
            episodeNumber={episodeNumber}
            parentId={node.id}
            autoFocus={true}
            onCancel={() => setReplying(false)}
            onPosted={() => { setReplying(false); if (onChanged) onChanged(); }}
          />
        </div>
      )}
    </div>
  );
};

const flattenTree = (nodes, depth, out) => {
  for (const n of nodes) {
    out.push({ ...n, _depth: depth });
    if (n.replies && n.replies.length) flattenTree(n.replies, depth + 1, out);
  }
  return out;
};

const CommentThread = ({ episodeNumber, comments, onChanged }) => {
  const { user } = useAuth();
  const flat = flattenTree(comments || [], 0, []);
  return (
    <div className="space-y-3" data-testid={"comment-thread-" + episodeNumber}>
      {user ? (
        <ReplyForm episodeNumber={episodeNumber} onPosted={onChanged} />
      ) : (
        <p className="scribble text-white/60 text-lg">log in to leave a comment ↑</p>
      )}
      {flat.length === 0 && (
        <p className="text-white/40 font-outfit text-sm italic">no comments yet — be the first to spill ♡</p>
      )}
      <div className="space-y-3">
        {flat.map((n) => (
          <CommentRow key={n.id} node={n} episodeNumber={episodeNumber} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
};

export default CommentThread;
